/**
 * Rahmen der Anwendung: Navigation links, Kopfzeile oben.
 *
 * Die Navigation zeigt nur Bereiche, fuer die der angemeldete Benutzer
 * eine Berechtigung hat. Das ist reine Bequemlichkeit - die eigentliche
 * Pruefung findet auf dem Server statt.
 */

import { useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import type { ModuleKey, Permission } from '@erp/shared';

import { useAuth } from '../auth';
import { NotificationBell } from './NotificationBell';
import { ThemeButton } from './theme';
import { useToast } from './toast';

type NavEntry = {
  to: string;
  label: string;
  icon: string;
  permission: Permission;
  /** Gehoert der Eintrag zu einem Fachmodul, erscheint er nur bei aktivem Modul. */
  module?: ModuleKey;
};

type NavGroup = {
  title: string;
  /** Gilt fuer die ganze Gruppe - etwa einen kuenftigen Bereich „Mietpark". */
  module?: ModuleKey;
  entries: NavEntry[];
};

const NAVIGATION: NavGroup[] = [
  {
    title: 'Übersicht',
    entries: [
      { to: '/', label: 'Startseite', icon: '▤', permission: 'masterdata:read' },
      { to: '/auswertungen', label: 'Auswertungen', icon: '▥', permission: 'sales:read' },
      { to: '/auswertungen/berichte', label: 'Berichte', icon: '≣', permission: 'reports:read' },
    ],
  },
  {
    title: 'Verkauf',
    entries: [
      { to: '/verkauf/angebote', label: 'Angebote', icon: '✎', permission: 'sales:read' },
      { to: '/verkauf/auftraege', label: 'Aufträge', icon: '⊞', permission: 'sales:read' },
      { to: '/verkauf/rahmenvertraege', label: 'Rahmenverträge', icon: '§', permission: 'sales:read' },
      { to: '/verkauf/lieferscheine', label: 'Lieferscheine', icon: '⇥', permission: 'sales:read' },
      { to: '/verkauf/rechnungen', label: 'Rechnungen', icon: '€', permission: 'sales:read' },
      {
        to: '/reklamationen',
        label: 'Reklamationen',
        icon: '⚑',
        permission: 'complaints:read',
        module: 'complaints',
      },
    ],
  },
  {
    title: 'CRM',
    module: 'crm',
    entries: [
      {
        to: '/crm/wiedervorlagen',
        label: 'Wiedervorlagen',
        icon: '☏',
        permission: 'crm:read',
      },
      {
        to: '/crm/aktivitaeten',
        label: 'Aktivitäten',
        icon: '✆',
        permission: 'crm:read',
      },
    ],
  },
  {
    title: 'Werkstatt',
    module: 'repairs',
    entries: [
      {
        to: '/werkstatt/reparaturen',
        label: 'Reparaturen',
        icon: '⚒',
        permission: 'repairs:read',
      },
    ],
  },
  {
    title: 'Einkauf & Lager',
    entries: [
      { to: '/einkauf/bestellungen', label: 'Bestellungen', icon: '⊟', permission: 'purchase:read' },
      { to: '/einkauf/wareneingang', label: 'Wareneingang', icon: '⇤', permission: 'purchase:read' },
      {
        to: '/einkauf/eingangsrechnungen',
        label: 'Eingangsrechnungen',
        icon: '₪',
        permission: 'purchase:read',
      },
      {
        to: '/einkauf/erechnung-import',
        label: 'E-Rechnung einlesen',
        icon: '⇧',
        permission: 'purchase:write',
      },
      {
        to: '/einkauf/bestellvorschlag',
        label: 'Bestellvorschlag',
        icon: '☑',
        permission: 'purchase:read',
      },
      { to: '/lager/bestand', label: 'Lagerbestand', icon: '▦', permission: 'stock:read' },
    ],
  },
  {
    title: 'Stammdaten',
    entries: [
      { to: '/kunden', label: 'Kunden', icon: '☰', permission: 'masterdata:read' },
      { to: '/lieferanten', label: 'Lieferanten', icon: '☰', permission: 'masterdata:read' },
      { to: '/artikel', label: 'Artikel', icon: '▣', permission: 'masterdata:read' },
      { to: '/artikel/sets', label: 'Set-Artikel', icon: '▦', permission: 'masterdata:read' },
    ],
  },
  {
    title: 'Buchhaltung',
    entries: [
      { to: '/buchhaltung/journal', label: 'Buchungsjournal', icon: '☷', permission: 'accounting:read' },
      { to: '/buchhaltung/zahlungen', label: 'Zahlungen', icon: '⇄', permission: 'accounting:read' },
      { to: '/buchhaltung/kontoauszug', label: 'Kontoauszug', icon: '🏦', permission: 'accounting:read' },
      { to: '/buchhaltung/datev', label: 'DATEV-Export', icon: '⇩', permission: 'accounting:export' },
    ],
  },
  {
    title: 'Verwaltung',
    entries: [
      { to: '/einstellungen', label: 'Einstellungen', icon: '⚙', permission: 'settings:read' },
      { to: '/benutzer', label: 'Benutzer', icon: '☺', permission: 'users:manage' },
      { to: '/protokoll', label: 'Protokoll', icon: '⌸', permission: 'audit:read' },
    ],
  },
];

export function Layout() {
  const { user, can, moduleEnabled, appLinks, logout } = useAuth();

  // Schwesteranwendungen: nur wer dort Zugang hat, sieht den Verweis.
  const appEntries = [
    { href: appLinks.wissen, label: 'Wissensdatenbank', icon: 'W', permission: 'wissen:access' as Permission },
    { href: appLinks.portal, label: 'Außendienst-Portal', icon: 'A', permission: 'portal:access' as Permission },
  ].filter((entry): entry is typeof entry & { href: string } => Boolean(entry.href) && can(entry.permission));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const toast = useToast();

  async function handleLogout() {
    try {
      await logout();
      navigate('/');
    } catch {
      toast.error('Die Abmeldung ist fehlgeschlagen.');
    }
  }

  return (
    <div className="app-shell">
      <div className="brand">
        <span className="brand-mark">C</span>
        <span>Canalis</span>
      </div>

      <header className="app-header">
        <div />
        <div className="row" style={{ gap: 14 }}>
          <div style={{ textAlign: 'right', lineHeight: 1.3 }}>
            <div style={{ fontWeight: 500 }}>{user?.displayName}</div>
            <div className="faint" style={{ fontSize: 12 }}>
              {user?.groups.length ? user.groups.join(', ') : 'ohne Gruppe'}
            </div>
          </div>
          <NotificationBell />
          <ThemeButton className="btn btn-sm btn-quiet" />
        </div>
      </header>

      <nav className="app-sidebar">
        {NAVIGATION.map((group) => {
          if (group.module && !moduleEnabled(group.module)) return null;

          const visible = group.entries.filter(
            (entry) =>
              can(entry.permission) && (!entry.module || moduleEnabled(entry.module)),
          );
          if (visible.length === 0) return null;

          return (
            <div className="nav-group" key={group.title}>
              <div className="nav-group-title">{group.title}</div>
              {visible.map((entry) => (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.to === '/'}
                  // "Artikel" darf nicht leuchten, wenn der Unterpunkt "Set-Artikel" offen ist.
                  className={({ isActive }) => `nav-item${isActive && !(entry.to === '/artikel' && pathname.startsWith('/artikel/sets')) ? ' active' : ''}`}
                >
                  <span className="nav-icon" aria-hidden="true">
                    {entry.icon}
                  </span>
                  {entry.label}
                </NavLink>
              ))}
            </div>
          );
        })}
        {appEntries.length > 0 ? (
          <div className="nav-group">
            <div className="nav-group-title">Anwendungen</div>
            {appEntries.map((entry) => (
              <a key={entry.label} className="nav-item" href={entry.href} target="_blank" rel="noreferrer">
                <span className="nav-icon" aria-hidden="true">
                  {entry.icon}
                </span>
                {entry.label}
              </a>
            ))}
          </div>
        ) : null}
        <div className="nav-footer">
          <button type="button" className="nav-item" onClick={handleLogout} title={user?.displayName}>
            <span className="nav-icon" aria-hidden="true">
              ⎋
            </span>
            Abmelden
          </button>
        </div>
      </nav>

      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}

/** Kopfzeile einer Seite mit Titel und Aktionen. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  // Der Tab-Titel traegt den Seitentitel, damit sich mehrere offene Canalis-Tabs
  // ("Auftrag AB-2026-0008", "Kunde Nordlicht Elektro") unterscheiden lassen.
  // Ohne den Zusatz "Canalis" - das Favicon zeigt die Anwendung, der knappe
  // Tab-Platz gehoert dem Belegtitel.
  useEffect(() => {
    document.title = title || 'Canalis';
    return () => {
      document.title = 'Canalis';
    };
  }, [title]);

  return (
    <div className="page-header">
      <div className="page-title-group">
        <h1>{title}</h1>
        {subtitle ? <span className="page-subtitle">{subtitle}</span> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </div>
  );
}
